'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  CrmFlowRun,
  CrmFlowRunSummary,
  CrmFlowStep,
  CrmTriggerJob,
  CrmTriggerResult,
  CrmTriggersStatus,
  ImportGranolaNoteInput,
  RunCrmFlowStepInput,
  RunCrmJobInput,
  SendTestSlackDmInput,
  SimulateOnboardingSignalInput,
  StartCrmFlowInput,
} from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

const BASE = 'admin-terminal/crm-triggers';

export const crmTriggersStatusQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.crmTriggersStatus(),
    queryFn: ({ signal }) =>
      apiClient.get<CrmTriggersStatus>(`${BASE}/status`, { signal }),
    staleTime: 60_000,
    // 404 is the answer on production: the page says so instead of retrying.
    retry: false,
  });

export const useCrmTriggersStatus = () =>
  useQuery(crmTriggersStatusQueryOptions());

/** One trigger request: `POST admin-terminal/crm-triggers/<path>` with its body. */
export type CrmTriggerRequest =
  | { kind: 'job'; job: CrmTriggerJob; input: RunCrmJobInput }
  | { kind: 'granola-import'; input: ImportGranolaNoteInput }
  | { kind: 'onboarding-signal'; input: SimulateOnboardingSignalInput }
  | { kind: 'slack-dm'; input: SendTestSlackDmInput };

const pathOf = (request: CrmTriggerRequest) =>
  request.kind === 'job'
    ? `${BASE}/jobs/${request.job}`
    : `${BASE}/${request.kind}`;

/** Fire one trigger and get the job's own result back, as the API returned it. */
export const useRunCrmTrigger = () => {
  const mutation = useMutation({
    mutationFn: (request: CrmTriggerRequest) =>
      apiClient.post<CrmTriggerResult>(pathOf(request), request.input),
  });
  return {
    run: mutation.mutateAsync,
    isRunning: mutation.isPending,
  };
};

/** The handoff flow test's recent runs, newest first. */
export const crmFlowRunsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.crmFlowRuns(),
    queryFn: ({ signal }) =>
      apiClient.get<CrmFlowRunSummary[]>(`${BASE}/flow`, { signal }),
    retry: false,
  });

export const useCrmFlowRuns = () => useQuery(crmFlowRunsQueryOptions());

/**
 * One run. While it waits on signing, it is re-read every 5 seconds: the
 * Twenty webhook adds the contract after the price step, and a real
 * signature lands on its own.
 */
export const crmFlowRunQueryOptions = (runId: string | null) =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.crmFlowRun(runId ?? ''),
    queryFn: ({ signal }) =>
      apiClient.get<CrmFlowRun>(`${BASE}/flow/${runId}`, { signal }),
    enabled: !!runId,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.next === 'sign' && !query.state.data.cleanedUp
        ? 5_000
        : false,
  });

export const useCrmFlowRun = (runId: string | null) =>
  useQuery(crmFlowRunQueryOptions(runId));

/** Start, step and clean up all answer with the run as it now stands. */
const useFlowMutation = <TInput>(
  request: (input: TInput) => Promise<CrmFlowRun>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (run) => {
      queryClient.setQueryData(
        queryKeys.adminTerminal.crmFlowRun(run.runId),
        run
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminTerminal.crmFlowRuns(),
      });
    },
  });
};

export const useStartCrmFlow = () => {
  const mutation = useFlowMutation((input: StartCrmFlowInput) =>
    apiClient.post<CrmFlowRun>(`${BASE}/flow`, input)
  );
  return { start: mutation.mutateAsync, isStarting: mutation.isPending };
};

export const useRunCrmFlowStep = () => {
  const mutation = useFlowMutation(
    ({
      runId,
      step,
      ...input
    }: RunCrmFlowStepInput & { runId: string; step: CrmFlowStep }) =>
      apiClient.post<CrmFlowRun>(`${BASE}/flow/${runId}/steps/${step}`, input)
  );
  return { runStep: mutation.mutateAsync, isRunningStep: mutation.isPending };
};

export const useCleanUpCrmFlow = () => {
  const mutation = useFlowMutation((runId: string) =>
    apiClient.post<CrmFlowRun>(`${BASE}/flow/${runId}/cleanup`, {})
  );
  return { cleanUp: mutation.mutateAsync, isCleaningUp: mutation.isPending };
};
